import React, { forwardRef, Suspense, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { PerspectiveCamera } from '@react-three/drei';
import { cn } from '../../lib/utils';

function Globe({ rotationSpeed, radius }) {
  const groupRef = useRef(null);

  useFrame(() => {
    if (!groupRef.current) return;
    groupRef.current.rotation.y += rotationSpeed;
    groupRef.current.rotation.x += rotationSpeed * 0.3;
    groupRef.current.rotation.z += rotationSpeed * 0.1;
  });

  return (
    <group ref={groupRef}>
      <mesh>
        <sphereGeometry args={[radius, 64, 64]} />
        <meshBasicMaterial
          color="#47b0ff"
          transparent
          opacity={0.2}
          wireframe
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

/** Full-viewport hero with wireframe globe (WebGL). Children render centered above canvas. */
const DotGlobeHero = forwardRef(function DotGlobeHero(
  {
    rotationSpeed = 0.005,
    globeRadius = 1,
    className,
    children,
    ...props
  },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(
        'relative w-full min-h-screen overflow-hidden bg-black',
        className
      )}
      {...props}
    >
      <div className="pointer-events-none absolute inset-0 z-0">
        <Suspense fallback={null}>
          <Canvas
            dpr={[1, 2]}
            gl={{ antialias: true, alpha: false }}
            style={{ width: '100%', height: '100%', display: 'block' }}
          >
            <color attach="background" args={['#000000']} />
            <PerspectiveCamera makeDefault position={[0, 0, 3]} fov={75} />
            <ambientLight intensity={0.5} />
            <pointLight position={[10, 10, 10]} intensity={1} />
            <Globe rotationSpeed={rotationSpeed} radius={globeRadius} />
          </Canvas>
        </Suspense>
      </div>

      <div className="relative z-10 flex min-h-[100svh] w-full flex-col items-center justify-center px-4 pb-14 pt-[7.5rem] md:pb-16 md:pt-32">
        {children}
      </div>
    </div>
  );
});

DotGlobeHero.displayName = 'DotGlobeHero';

export { DotGlobeHero };
